import { useEffect, useRef, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import useUserStore from '../../store/userStore';
import useNotificationStore from '../../store/notificationStore';
import { baseURL } from '../../lib/apiClient';

const WebSocketManager = () => {
    const { user } = useUserStore();
    const { addNotification } = useNotificationStore();
    const clientRef = useRef<Client | null>(null);

    // ✨ 1. handleMessage를 useEffect보다 위로 올리고 useCallback으로 감쌉니다.
    // 이렇게 하면 addNotification이 변하지 않는 한 이 함수는 재생성되지 않습니다.
    const handleMessage = useCallback((body: string) => {
        let title = '새로운 알림 도착! 🔔';
        let content = '';

        try {
            // 1. 일단 JSON 파싱 시도
            const data = JSON.parse(body);

            if (typeof data === 'object' && data !== null) {
                // 2-1. 객체인 경우 (예: { "msg": "안녕하세요", "title": "공지" })
                content = data.msg || data.message || data.content || JSON.stringify(data);
                if (data.title) {
                    title = data.title;
                }
            } else {
                // 2-2. JSON이지만 단순 문자열/숫자인 경우 (예: "안녕하세요")
                content = String(data);
            }
        } catch (e) {
            // 3. JSON 파싱 실패 시 (예: Quartz에서 발송한...) -> 평문으로 처리
            // 에러를 무시하고 원본 body를 그대로 사용합니다.
            content = body;
        }

        addNotification({
            title,
            content,
        });
    }, [addNotification]);

    useEffect(() => {
        // 1. 유저가 없으면 연결하지 않음 (또는 연결 해제)
        if (!user) {
            if (clientRef.current) {
                clientRef.current.deactivate();
                clientRef.current = null;
            }
            return;
        }

        // 2. 이미 연결되어 있으면 스킵
        if (clientRef.current && clientRef.current.active) return;

        // 3. 쿠키에서 accessToken 추출
        const getCookie = (name: string) => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop()?.split(';').shift();
        };

        const token = getCookie('accessToken') || '';

        // 4. STOMP 클라이언트 설정
        const client = new Client({
            webSocketFactory: () => new SockJS(`${baseURL}/socket/alarm`),
            connectHeaders: {
                Authorization: `Bearer ${token}`,
            },
            debug: (str) => {
                if (process.env.NODE_ENV === 'development') {
                    console.log('STOMP: ' + str);
                }
            },
            onConnect: () => {
                console.log('✅ WebSocket Connected!');

                client.subscribe('/topic/alarm', (message) => {
                    handleMessage(message.body);
                });

                client.subscribe('/user/queue/alarm', (message) => {
                    handleMessage(message.body);
                });
            },
            onStompError: (frame) => {
                console.error('Broker reported error: ' + frame.headers['message']);
                console.error('Additional details: ' + frame.body);
            },
        });

        client.activate();
        clientRef.current = client;

        return () => {
            if (clientRef.current) {
                clientRef.current.deactivate();
                console.log('❌ WebSocket Disconnected');
            }
        };
        // ✨ 2. 의존성 배열에 handleMessage를 추가합니다.
    }, [user, handleMessage]);

    return null;
};

export default WebSocketManager;